import api from './api';
import { nowCv } from '../utils/date';

/**
 * Contratos de promotor — condições comerciais.
 *
 * Estas taxas eram, até 07/08/2026, apenas alteráveis por SQL. O único ecrã que
 * existia (events.cv/super-admin/contracts) mostrava o texto jurídico com «8%»
 * escrito à mão no código, e a data de início como cadeia vazia — razão pela qual
 * ninguém notou que o contrato do Sigui Sabura só entra em vigor a 01/09/2026,
 * enquanto o Réveillon é a 31/12.
 *
 * A API (/v1/pass/contracts) já suportava tudo isto, incluindo versionamento.
 */

export type EstadoContrato = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'TERMINATED';
/**
 * Os instrumentos que o backend conhece (TOPUP_INSTRUMENTS em pass-contracts.ts).
 * A primeira versao deste ficheiro tinha CARD/WALLET/MOBILE, que eu adivinhei — e uma
 * taxa gravada num instrumento inexistente nunca seria aplicada a nada.
 */
export type Instrumento = 'VINTI4' | 'VISA' | 'MASTERCARD' | 'CASH' | 'BANK_TRANSFER';
export type Portador = 'PROMOTER' | 'PARTICIPANT';

/** Um escalão de bilheteira: até `maxTickets`, aplica-se `rateBps`. */
/** Um conjunto de clausulas publicado: o padrao, ou uma excepcao negociada. */
/**
 * Os serviços com contrato. A lista vive no backend (services/servicos-contrato.ts) e
 * chega por GET /pass/servicos — aqui não se escreve, para não haver duas que divergem.
 */
export type Servico = string;
export interface DefServico {
  codigo: Servico;
  nome: string;
  /** O que se conta nos escalões: bilhetes, vouchers, vendas… */
  unidade: string;
  /** Quem paga a Pagali: a ENTIDADE (comissão retida) ou a SISP (recargas, facturas…). */
  remuneracao: 'ENTIDADE' | 'SISP';
  /** Taxa à entidade por omissão. 0 nos serviços pagos pela SISP. */
  omissaoBps: number;
  /** Omissões dos campos genéricos do contrato deste serviço. */
  contratoOmissao?: {
    comissao_base?: 'BRUTO' | 'SUBTOTAL';
    taxa_fixa_cve?: number;
    taxa_entrega_cve?: number | null;
    valor_estafeta_cve?: number | null;
    liquidacao_periodicidade?: 'DIARIA' | 'SEMANAL' | 'MENSAL';
    liquidacao_dia?: number;
  };
}
/** Para um código que a lista ainda não trouxe (ou que deixou de existir). */
export const defDesconhecida = (codigo: Servico): DefServico =>
  ({ codigo, nome: codigo, unidade: 'vendas', remuneracao: 'ENTIDADE', omissaoBps: 0 });

export interface ConjuntoClausulas {
  id: string;
  servico: Servico;
  version: number;
  ePadrao: boolean;
  notas: string | null;
  clausulas: number;
  contratos: number;
}

export interface Escalao {
  tierIndex: number;
  /** null = sem limite, ou seja o último escalão. */
  maxTickets: number | null;
  rateBps: number;
}

export interface TaxaCarregamento {
  instrument: Instrumento;
  rateBps: number;
}

export interface Contrato {
  id: string;
  promoterEntityId: string;
  servico: Servico;
  promotorNome?: string;
  version: number;
  status: EstadoContrato;
  effectiveFrom: string;
  effectiveTo: string | null;
  contractYearStartMD: string;
  /** Tecto de comissão POR BILHETE, em CVE. null = sem tecto. */
  perTicketCapCVE: number | null;
  /** Isento da comissão PERCENTUAL. Um convite está sempre isento, porque o preço é
   *  zero — mas pode ainda assim pagar a taxa fixa abaixo. São coisas distintas. */
  freeTicketsExempt: boolean;
  /** Taxa FIXA em CVE por convite. 0 = convites sem custo. */
  freeTicketFeeCVE: number;
  /** Quando a taxa do convite é devida. */
  freeTicketFeeOn: 'CHECKIN' | 'ISSUE';
  vatRateBps: number;
  consumptionRateBps: number;
  transferRateBps: number;
  topupBearer: Portador;
  topupDefaultRateBps: number;
  topupMinCVE: number | null;
  topupMaxCVE: number | null;
  /** Dia da semana da transferência de bilheteira. 0 = domingo, 1 = segunda. */
  settlementDow: number;
  /** Dias após o evento para liquidar o cashless. */
  settlementCashlessDias: number;
  escaloes: Escalao[];
  taxasCarregamento: TaxaCarregamento[];
  /** Taxa SISP: INCLUDED na comissão, ou PASS_THROUGH (cobrada à parte, valor real da SISP). */
  sispFeeMode: 'INCLUDED' | 'PASS_THROUGH';
  /** IVA sobre o repasse da taxa SISP. */
  sispFeeVat: boolean;
  /** Taxas cobradas ao promotor (bps) por cartão. null = repassa o valor real da SISP. */
  sispRateVinti4Bps: number | null;
  sispRateIntlBps: number | null;
  /** Campos genéricos — servem qualquer serviço (base, taxa fixa, entrega, liquidação). */
  comissaoBase: 'BRUTO' | 'SUBTOTAL';
  taxaFixaCVE: number;
  /** Pagali Food: taxa de entrega cobrada ao cliente; null = não se aplica. */
  taxaEntregaCVE: number | null;
  /** Pagali Food: excepção à tabela global da taxa de entrega (null = global). */
  entregaModo: 'FIXO' | 'DISTANCIA' | null;
  entregaBaseCVE: number | null;
  entregaKmIncluidos: number | null;
  entregaPrecoKmCVE: number | null;
  /** Pagali Food: o que o estafeta recebe por entrega. */
  valorEstafetaCVE: number | null;
  liquidacaoPeriodicidade: 'DIARIA' | 'SEMANAL' | 'MENSAL' | null;
  liquidacaoDia: number | null;
  tierCount?: number;
  /** Que texto este contrato usa. Só vem no detalhe, não na listagem. */
  clausulas?: {
    templateId: string | null;
    version: number | null;
    ePadrao: boolean;
    origem: 'CONTRATO' | 'PADRAO' | 'NENHUM';
    total: number;
  };
}

/** bps → percentagem legível. 600 → "6%", 550 → "5,5%" */
export const bpsParaPercento = (bps: number): string => {
  const pct = bps / 100;
  return `${String(pct).replace('.', ',')}%`;
};

/** percentagem → bps, aceitando vírgula decimal. "5,5" → 550 */
export const percentoParaBps = (txt: string): number => {
  const n = parseFloat(String(txt).replace(',', '.'));
  if (!Number.isFinite(n)) return NaN;
  return Math.round(n * 100);
};

/**
 * A comissão de um bilhete, calculada como o backend a calcula.
 *
 * Existe para o ecrã poder mostrar o efeito real antes de gravar: um tecto de 250
 * CVE não se percebe numa tabela de percentagens, percebe-se ao ver que um bilhete
 * VIP de 25.000 paga 250 e não 1.500.
 */
export function comissaoDoBilhete(
  precoCVE: number, contrato: Contrato, bilhetesJaVendidos = 0,
): { rateBps: number; brutaCVE: number; comissaoCVE: number; tectoPegou: boolean; escalao: number } {
  const ordenados = [...contrato.escaloes].sort((a, b) => a.tierIndex - b.tierIndex);
  let escolhido = ordenados[ordenados.length - 1];
  for (const e of ordenados) {
    if (e.maxTickets === null || bilhetesJaVendidos < e.maxTickets) { escolhido = e; break; }
  }
  const rateBps = escolhido?.rateBps ?? 0;
  const bruta = Math.round((precoCVE * rateBps) / 10000);
  const tecto = contrato.perTicketCapCVE;
  const comissao = tecto != null && bruta > tecto ? tecto : bruta;
  return {
    rateBps,
    brutaCVE: bruta,
    comissaoCVE: comissao,
    tectoPegou: tecto != null && bruta > tecto,
    escalao: escolhido?.tierIndex ?? 0,
  };
}

/** Está em vigor à data indicada? Estado ACTIVE não basta — a vigência é uma janela. */
// effectiveFrom/To são hora de parede de CV → comparar com «agora em CV».
export function emVigor(c: Contrato, quando: Date = nowCv()): boolean {
  if (c.status !== 'ACTIVE') return false;
  if (new Date(c.effectiveFrom) > quando) return false;
  if (c.effectiveTo && new Date(c.effectiveTo) <= quando) return false;
  return true;
}

/**
 * Duas formas para a mesma coisa, e sabe-lo evita erros:
 *
 *   GET /contracts/:promoterEntityId        devolve LINHAS CRUAS (snake_case) com status
 *   GET /contracts/detail/:contractId       devolve o objecto JÁ CONSTRUÍDO (camelCase),
 *                                           com escalões e taxas — mas SEM status
 *
 * Por isso o detalhe recebe o status da listagem: não o inventa nem o assume ACTIVE.
 */
function deLinha(row: any): Contrato {
  return {
    id: row.id,
    promoterEntityId: row.promoter_entity_id,
    servico: (row.servico ?? 'EVENTOS') as Servico,
    promotorNome: row.promotor_nome ?? undefined,
    version: Number(row.version),
    status: row.status,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to ?? null,
    contractYearStartMD: row.contract_year_start_md,
    perTicketCapCVE: row.ticket_per_ticket_cap_cve ?? null,
    freeTicketsExempt: row.ticket_free_exempt === 1,
    freeTicketFeeCVE: Number(row.ticket_free_fee_cve ?? 0),
    freeTicketFeeOn: (row.ticket_free_fee_on ?? 'CHECKIN') as 'CHECKIN' | 'ISSUE',
    vatRateBps: Number(row.vat_rate_bps ?? 0),
    consumptionRateBps: Number(row.consumption_rate_bps ?? 0),
    transferRateBps: Number(row.transfer_rate_bps ?? 0),
    topupBearer: row.topup_bearer,
    topupDefaultRateBps: Number(row.topup_default_rate_bps ?? 0),
    topupMinCVE: row.topup_min_cve ?? null,
    topupMaxCVE: row.topup_max_cve ?? null,
    settlementDow: Number(row.settlement_ticketing_dow ?? 1),
    settlementCashlessDias: Number(row.settlement_cashless_days ?? 5),
    escaloes: [],
    taxasCarregamento: [],
    sispFeeMode: row.sisp_fee_mode === 'PASS_THROUGH' ? 'PASS_THROUGH' : 'INCLUDED',
    sispFeeVat: Number(row.sisp_fee_vat ?? 0) === 1,
    sispRateVinti4Bps: row.sisp_rate_vinti4_bps == null ? null : Number(row.sisp_rate_vinti4_bps),
    sispRateIntlBps: row.sisp_rate_intl_bps == null ? null : Number(row.sisp_rate_intl_bps),
    comissaoBase: row.comissao_base === 'SUBTOTAL' ? 'SUBTOTAL' : 'BRUTO',
    taxaFixaCVE: Number(row.taxa_fixa_cve ?? 0),
    taxaEntregaCVE: row.taxa_entrega_cve == null ? null : Number(row.taxa_entrega_cve),
    entregaModo: row.entrega_modo ?? null,
    entregaBaseCVE: row.entrega_base_cve == null ? null : Number(row.entrega_base_cve),
    entregaKmIncluidos: row.entrega_km_incluidos == null ? null : Number(row.entrega_km_incluidos),
    entregaPrecoKmCVE: row.entrega_preco_km_cve == null ? null : Number(row.entrega_preco_km_cve),
    valorEstafetaCVE: row.valor_estafeta_cve == null ? null : Number(row.valor_estafeta_cve),
    liquidacaoPeriodicidade: row.liquidacao_periodicidade ?? null,
    liquidacaoDia: row.liquidacao_dia == null ? null : Number(row.liquidacao_dia),
    tierCount: row.tier_count != null ? Number(row.tier_count) : undefined,
  };
}

function deObjecto(k: any, status: EstadoContrato, servico: Servico): Contrato {
  return {
    id: k.id,
    promoterEntityId: k.promoterId,
    servico,
    version: Number(k.version),
    status,
    effectiveFrom: k.effectiveFrom,
    effectiveTo: k.effectiveTo ?? null,
    contractYearStartMD: k.contractYearStartMonthDay,
    perTicketCapCVE: k.ticketing?.perTicketCapCVE ?? null,
    freeTicketsExempt: !!k.ticketing?.freeTicketsExempt,
    freeTicketFeeCVE: Number(k.ticketing?.freeTicketFeeCVE ?? 0),
    freeTicketFeeOn: (k.ticketing?.freeTicketFeeOn ?? 'CHECKIN') as 'CHECKIN' | 'ISSUE',
    vatRateBps: Number(k.vat?.rateBps ?? 0),
    consumptionRateBps: Number(k.cashless?.consumptionCommissionBps ?? 0),
    transferRateBps: Number(k.cashless?.transferCommissionBps ?? 0),
    topupBearer: k.topUp?.bearer,
    topupDefaultRateBps: Number(k.topUp?.defaultRateBps ?? 0),
    topupMinCVE: k.topUp?.minTopUpCVE ?? null,
    topupMaxCVE: k.topUp?.maxTopUpCVE ?? null,
    settlementDow: Number(k.settlement?.ticketingDayOfWeek ?? 1),
    settlementCashlessDias: Number(k.settlement?.cashlessDays ?? 5),
    // volumeTiers vem SEM indice, pela ordem. O indice e a posicao.
    escaloes: (k.ticketing?.volumeTiers ?? []).map((t: any, i: number) => ({
      tierIndex: i, maxTickets: t.maxTickets ?? null, rateBps: Number(t.rateBps),
    })),
    taxasCarregamento: Object.entries(k.topUp?.ratesBps ?? {}).map(([instrument, rate]) => ({
      instrument: instrument as Instrumento, rateBps: Number(rate),
    })),
    sispFeeMode: k.sispFee?.mode === 'PASS_THROUGH' ? 'PASS_THROUGH' : 'INCLUDED',
    sispFeeVat: !!k.sispFee?.vatOnFee,
    sispRateVinti4Bps: k.sispFee?.rateVinti4Bps ?? null,
    sispRateIntlBps: k.sispFee?.rateIntlBps ?? null,
    comissaoBase: k.geral?.comissaoBase === 'SUBTOTAL' ? 'SUBTOTAL' : 'BRUTO',
    taxaFixaCVE: Number(k.geral?.taxaFixaCVE ?? 0),
    taxaEntregaCVE: k.geral?.taxaEntregaCVE ?? null,
    entregaModo: k.geral?.entregaModo ?? null,
    entregaBaseCVE: k.geral?.entregaBaseCVE ?? null,
    entregaKmIncluidos: k.geral?.entregaKmIncluidos ?? null,
    entregaPrecoKmCVE: k.geral?.entregaPrecoKmCVE ?? null,
    valorEstafetaCVE: k.geral?.valorEstafetaCVE ?? null,
    liquidacaoPeriodicidade: k.geral?.liquidacaoPeriodicidade ?? null,
    liquidacaoDia: k.geral?.liquidacaoDia ?? null,
  };
}

export const contratosService = {
  async servicos(): Promise<DefServico[]> {
    const { data } = await api.get('/pass/servicos');
    if (!data?.success) throw new Error(data?.error || 'Falha a listar os serviços');
    return data.servicos ?? [];
  },

  /** As versões do contrato de uma entidade NUM SERVIÇO, da mais recente para a mais antiga. */
  async listar(promoterEntityId: string, servico: Servico): Promise<Contrato[]> {
    const { data } = await api.get(`/pass/contracts/${promoterEntityId}`, { params: { servico } });
    if (!data?.success) throw new Error(data?.error || 'Falha a listar contratos');
    return (data.contracts ?? []).map(deLinha);
  },

  /**
   * Detalhe com escalões e taxas. O `status` vem da listagem porque este endpoint
   * não o devolve — assumir ACTIVE mostraria em vigor um contrato já substituído.
   */
  async detalhe(contractId: string, status: EstadoContrato, servico: Servico): Promise<Contrato> {
    const { data } = await api.get(`/pass/contracts/detail/${contractId}`);
    if (!data?.success) throw new Error(data?.error || 'Contrato não encontrado');
    const c = deObjecto(data.contract, status, servico);
    const cl = data.clausulas;
    if (cl) {
      c.clausulas = {
        templateId: cl.templateId ?? null,
        version: cl.version ?? null,
        ePadrao: !!cl.ePadrao,
        origem: cl.origem ?? 'NENHUM',
        total: Array.isArray(cl.clausulas) ? cl.clausulas.length : 0,
      };
    }
    return c;
  },

  /**
   * Cria uma NOVA VERSÃO do contrato. Nunca reescreve a anterior: a versão em vigor
   * quando um bilhete foi vendido tem de continuar legível, ou o extracto do promotor
   * deixa de se poder justificar.
   */
  async criarVersao(payload: {
    promoterEntityId: string;
    servico: Servico;
    effectiveFrom: string;
    contractYearStartMD: string;
    perTicketCapCVE: number | null;
    freeTicketsExempt: boolean;
    ticketFreeFeeCVE: number;
    ticketFreeFeeOn: 'CHECKIN' | 'ISSUE';
    vatRateBps: number;
    consumptionRateBps: number;
    transferRateBps: number;
    topupBearer: Portador;
    topupDefaultRateBps: number;
    topupMinCVE: number | null;
    topupMaxCVE: number | null;
    settlementTicketingDow: number;
    settlementCashlessDays: number;
    // camelCase, que e o que o backend le. A primeira versao enviava snake_case e o
    // INSERT dos escaloes falhava com "Column 'tier_index' cannot be null" — terceira
    // vez que adivinhei a forma de um payload em vez de a ler no endpoint.
    tiers: Array<{ tierIndex: number; maxTickets: number | null; rateBps: number }>;
    topupRates: Array<{ instrument: Instrumento; rateBps: number }>;
    /**
     * Que conjunto de clausulas este contrato usa. Omitir = o padrao em vigor hoje,
     * que o backend fixa no contrato — nao o resolve na leitura, senao corrigir o
     * padrao no ano seguinte mudava o texto de um contrato ja assinado.
     */
    clausulasTemplateId?: string | null;
    sispFeeMode: 'INCLUDED' | 'PASS_THROUGH';
    sispFeeVat: boolean;
    sispRateVinti4Bps?: number | null;
    sispRateIntlBps?: number | null;
    comissaoBase?: 'BRUTO' | 'SUBTOTAL';
    taxaFixaCVE?: number;
    taxaEntregaCVE?: number | null;
    entregaModo?: 'FIXO' | 'DISTANCIA' | null;
    entregaBaseCVE?: number | null;
    entregaKmIncluidos?: number | null;
    entregaPrecoKmCVE?: number | null;
    valorEstafetaCVE?: number | null;
    liquidacaoPeriodicidade?: 'DIARIA' | 'SEMANAL' | 'MENSAL' | null;
    liquidacaoDia?: number | null;
  }): Promise<string> {
    const { data } = await api.post('/pass/contracts', payload);
    if (!data?.success) throw new Error(data?.error || 'Falha a criar contrato');
    return data.contractId ?? data.id;
  },

  /**
   * Corrigir um contrato que ainda NAO vigorou.
   *
   * A regra «um contrato nao se reescreve, cria-se uma versao nova» vale para um contrato
   * que vigorou: reescreve-lo apagava historia, e a clausula 4 assenta em saber que termos
   * valiam em cada data. Um DRAFT nunca vigorou e nao tem historia para apagar — trata-lo
   * como imutavel obrigava a apagar e recriar para mudar uma percentagem.
   *
   * O backend recusa com 409 se o contrato tiver saido de DRAFT ou tiver comissoes
   * lancadas. A mensagem dele e a que se mostra: repeti-la aqui era arriscar que as duas
   * divergissem.
   */
  async editarRascunho(
    contractId: string,
    payload: Partial<Parameters<typeof contratosService.criarVersao>[0]>,
  ): Promise<void> {
    const { data } = await api.patch(`/pass/contracts/${encodeURIComponent(contractId)}`, payload);
    if (!data?.success) throw new Error(data?.error || 'Falha a corrigir o rascunho');
  },

  /**
   * Os conjuntos de clausulas PUBLICADOS, para escolher qual o contrato usa.
   *
   * So publicados: apontar um contrato para um rascunho deixava o promotor a ler texto
   * que ainda estava a ser escrito. O backend recusa-o tambem, e por isso nem se
   * oferece na lista.
   */
  async conjuntosDeClausulas(servico: Servico): Promise<ConjuntoClausulas[]> {
    const { data } = await api.get('/contract-clausulas');
    if (!data?.success) throw new Error(data?.error || 'Falha a listar as clausulas');
    // So os do mesmo servico: o texto da bilheteira num contrato de vouchers nao
    // descreve o que se contratou, e o backend recusa-o.
    return (data.versoes ?? [])
      .filter((v: any) => v.status === 'PUBLISHED' && (v.servico ?? 'EVENTOS') === servico)
      .map((v: any) => ({
        id: v.id,
        servico: (v.servico ?? 'EVENTOS') as Servico,
        version: Number(v.version),
        ePadrao: Number(v.e_padrao) === 1,
        notas: v.notas ?? null,
        clausulas: Number(v.clausulas ?? 0),
        contratos: Number(v.contratos ?? 0),
      }));
  },

  async mudarEstado(contractId: string, status: EstadoContrato): Promise<void> {
    const { data } = await api.patch(`/pass/contracts/${contractId}/status`, { status });
    if (!data?.success) throw new Error(data?.error || 'Falha a mudar o estado');
  },
};
